import { useAPI } from '../hooks/useAPI';
import type { RoutesSummary, Shipment } from '../types';
import { MetricCard } from './shared/MetricCard';
import { formatCurrency, formatNumber, statusBadgeClass, titleCase, formatDate } from '../utils/format';

export function Transportation() {
  const summary = useAPI<RoutesSummary>('/api/routes/summary');
  const routes = useAPI<{ items: Shipment[] }>('/api/routes?limit=10&sort=eta&order=asc');

  if (summary.loading || routes.loading) return <p className="text-slate-500">Loading transportation…</p>;
  if (summary.error) return <p className="text-rose-600">Failed to load: {summary.error}</p>;
  if (!summary.data) return null;

  const t = summary.data.totals;
  const onTimeRate = t.delivered + t.delayed > 0 ? t.delivered / (t.delivered + t.delayed) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Total Shipments" value={formatNumber(t.total)} />
        <MetricCard label="In Transit" value={formatNumber(t.in_transit)} tone="warn" />
        <MetricCard label="Delayed" value={formatNumber(t.delayed)} tone={t.delayed > 0 ? 'bad' : 'good'} />
        <MetricCard label="Freight Spend" value={formatCurrency(t.total_cost)} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">By Mode</h2>
          <ul className="space-y-2">
            {summary.data.by_mode.map((m) => (
              <li key={m.mode} className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">{titleCase(m.mode)}</span>
                <span className="text-slate-500">
                  {formatNumber(m.count)} · {formatCurrency(m.cost)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-slate-400">
            Delivered-on-time rate: {(onTimeRate * 100).toFixed(0)}%
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Carrier Performance</h2>
          <ul className="divide-y divide-slate-100">
            {summary.data.by_carrier.map((c) => (
              <li key={c.carrier} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-slate-700">{c.carrier}</span>
                <span className="text-slate-500">
                  {c.delivered} delivered · {c.delayed} delayed
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="card overflow-x-auto">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Upcoming / Active Shipments</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="pb-2">Ref</th>
              <th className="pb-2">Origin</th>
              <th className="pb-2">Destination</th>
              <th className="pb-2">Carrier</th>
              <th className="pb-2">Status</th>
              <th className="pb-2 text-right">ETA</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {routes.data?.items.map((s) => (
              <tr key={s.id}>
                <td className="py-2 font-medium text-slate-800">{s.reference}</td>
                <td className="py-2 text-slate-500">{s.origin_name ?? '—'}</td>
                <td className="py-2 text-slate-500">{s.destination}</td>
                <td className="py-2 text-slate-500">{s.carrier}</td>
                <td className="py-2">
                  <span className={`badge ${statusBadgeClass(s.status)}`}>{titleCase(s.status)}</span>
                </td>
                <td className="py-2 text-right text-slate-600">{formatDate(s.eta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
